import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import AddIcon from "@mui/icons-material/Add";
import AccessibilityNewOutlinedIcon from "@mui/icons-material/AccessibilityNewOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import ViewInArOutlinedIcon from "@mui/icons-material/ViewInArOutlined";
import classNames from "classnames";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { selectUsedProps } from "../../app/slices/PropLibrarySlice";
import { selectActors } from "../../app/slices/TrainingSlice";
import { usePiChipNeutral } from "../../components/PiChip";
import PiChipSelect, { PiChipSelectOption } from "../../components/PiChipSelect";
import { usePiColorScope } from "../../components/PiColorScope";
import PropAvatar from "../../components/actors/PropAvatar";
import { ActorState } from "../../models/ActorState";
import { getPortalZoom } from "../../helpers/screenScale";
import useActorStateAttachments from "./useActorStateAttachments";

const PROP_TO_ACTOR = "prop_to_actor";
const ACTOR_TO_PROP = "actor_to_prop";

/**
 * Attachments as sentence rows: `[prop] [Goes on / Carries actor] at [tag]  delete`,
 * then a dashed "Attach prop" menu (Design Studio
 * `actor-settings/redesign-attach-full-v3`, "Attachments").
 */
export default function ActorStateAttachmentRows(props: { actorState: ActorState }) {
  const { actorState } = props;
  const { t } = useTranslation();
  const neutral = usePiChipNeutral();
  const [addOpen, setAddOpen] = useState(false);
  const addButtonRef = useRef<HTMLButtonElement>(null);
  // the menu portals out of the screen-scaling zoom, so it zooms itself to match the button
  const addMenuZoom = addOpen ? getPortalZoom(addButtonRef.current) : 1;
  const colorScope = usePiColorScope();
  const usedProps = useSelector(selectUsedProps);
  const actorName =
    useSelector(selectActors).find((actor) => actor.id === actorState.actorId)?.workingName ?? "";
  const {
    attachedProps,
    addAttachment,
    removeAttachment,
    changeAttachmentProp,
    updateAttachmentTag,
    updateAttachmentType,
    getAttachmentOptions,
  } = useActorStateAttachments(actorState);

  const propIcon = (propId: string) => (
    <PropAvatar src={usedProps[propId]?.propContent?.downloadUri} />
  );

  const directionOptions: PiChipSelectOption[] = [
    {
      id: PROP_TO_ACTOR,
      label: String(t("actor_settings.prop_to_actor", { actor: actorName })),
      icon: <AccessibilityNewOutlinedIcon className="flex-none" sx={{ fontSize: 18 }} />,
    },
    {
      id: ACTOR_TO_PROP,
      label: String(t("actor_settings.actor_to_prop", { actor: actorName })),
      icon: <ViewInArOutlinedIcon className="flex-none" sx={{ fontSize: 18 }} />,
    },
  ];

  const addOptions = getAttachmentOptions();

  return (
    <div className="flex min-h-0 flex-1 flex-col" data-testid="actor-state-attachment-rows">
      {/* the rows scroll on their own; the attach button stays in view below them */}
      <div className="flex min-h-0 flex-col gap-[4px] overflow-y-auto pit-creator-scrollbar" data-testid="actor-state-attachment-list">
        {attachedProps.map(({ attachment, propPreset, usedProp }) => {
          const playerToProp = attachment.playerToProp ?? false;
          const tag = attachment.tag ?? "";
          const tagOptions: PiChipSelectOption[] = (usedProp?.metadata.attachmentTags ?? []).map(
            (option) => ({
              id: option.tag,
              label: option.tag,
              icon: <LocalOfferOutlinedIcon className="flex-none" sx={{ fontSize: 18 }} />,
            })
          );

          return (
            <div
              key={propPreset.id}
              className="grid min-h-[56px] grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_auto_minmax(0,1fr)_auto] items-center gap-x-[10px]"
              data-testid={`attachment-row-${propPreset.id}`}
            >
              <PiChipSelect
                className="!w-full"
                aria-label={String(t("actor_settings.attachments"))}
                value={propPreset.id}
                options={getAttachmentOptions(propPreset.id).map((prop) => ({
                  id: prop.id,
                  label: prop.name,
                  icon: propIcon(prop.propId),
                }))}
                onChange={(nextPropId) =>
                  changeAttachmentProp(propPreset.id, nextPropId, playerToProp)
                }
                placeholder={propPreset.name}
              />
              <PiChipSelect
                tone="setting"
                className="!w-full"
                aria-label={`${propPreset.name} direction`}
                value={playerToProp ? ACTOR_TO_PROP : PROP_TO_ACTOR}
                options={directionOptions}
                onChange={(id) => updateAttachmentType(propPreset.id, tag, id === ACTOR_TO_PROP)}
                placeholder={directionOptions[0].label}
              />
              <span className="flex-none text-[13px] text-creator-text-sub">
                {String(t("actor_settings.at"))}
              </span>
              <PiChipSelect
                tone="setting"
                className="!w-full"
                aria-label={`${propPreset.name} tag`}
                value={tag}
                options={tagOptions}
                onChange={(nextTag) => updateAttachmentTag(propPreset.id, nextTag, playerToProp)}
                placeholder={String(t("actor_settings.no_tag"))}
                placeholderIcon={<LocalOfferOutlinedIcon className="flex-none" sx={{ fontSize: 18 }} />}
                disabled={tagOptions.length === 0}
              />
              <button
                type="button"
                aria-label={String(t("actor_settings.remove_attachment", { prop: propPreset.name }))}
                title={String(t("actor_settings.remove_attachment", { prop: propPreset.name }))}
                className="ml-auto flex h-[28px] w-[28px] flex-none cursor-pointer items-center justify-center rounded-full border-0 bg-transparent p-0 text-[18px] text-creator-text-sub transition-colors hover:bg-creator-surfaceHover hover:text-creator-error focus:outline-none focus-visible:ring-2 focus-visible:ring-interaction-primary"
                onClick={() => removeAttachment(propPreset.id)}
              >
                <DeleteOutlineIcon fontSize="inherit" color="inherit" />
              </button>
            </div>
          );
        })}
      </div>

      <DropdownMenu.Root open={addOpen} onOpenChange={setAddOpen}>
        <DropdownMenu.Trigger asChild disabled={addOptions.length === 0}>
          <button
            ref={addButtonRef}
            type="button"
            data-testid="add-attachment-button"
            className={classNames(
              "mt-[8px] inline-flex h-[40px] flex-none items-center gap-[6px] self-start rounded-[6px] border border-dashed bg-transparent px-[12px] text-[14px] font-semibold outline-none transition-colors",
              "focus-visible:border-interaction-primary",
              neutral ? "border-creator-border text-creator-text-sub" : "border-interaction-primary-faded text-interaction-primary",
              addOptions.length === 0
                ? "cursor-not-allowed opacity-60"
                : neutral
                  ? "cursor-pointer hover:border-creator-text-sub hover:bg-creator-surfaceHover"
                  : "cursor-pointer hover:border-interaction-primary hover:bg-interaction-primary-washed"
            )}
          >
            <AddIcon sx={{ fontSize: 18 }} />
            {String(t("actor_settings.attach_prop"))}
          </button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            align="start"
            sideOffset={6}
            collisionPadding={8}
            className={classNames(
              "z-[1500] flex min-w-[220px] max-w-[300px] max-h-[240px] flex-col overflow-y-auto pit-scrollbar",
              "rounded-[6px] border border-solid border-creator-border bg-creator-surface py-1.5",
              "shadow-[0_4px_12px_rgba(16,24,40,0.08)] outline-none",
              colorScope
            )}
            style={{ zoom: addMenuZoom }}
          >
            {addOptions.map((prop) => (
              <DropdownMenu.Item
                key={prop.id}
                onSelect={() => addAttachment(prop.id)}
                className="flex min-h-[36px] cursor-pointer select-none items-center gap-3 px-3 py-1 text-[14px] text-creator-text outline-none data-[highlighted]:bg-interaction-primary-washed"
              >
                {propIcon(prop.propId)}
                <span className="min-w-0 truncate">{prop.name}</span>
              </DropdownMenu.Item>
            ))}
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </div>
  );
}
